import {
    autoUpdate,
    flip,
    offset,
    type Placement,
    shift,
    useFloating,
} from "@floating-ui/react-dom";
import styled from "styled-components";
import Portal from "../../common/components/Portal";
import { palette, shadows, zIndex } from "../../styles/constants";
import useInert from "../../util/useInert";
import { useSheetContext } from "../Sheet/Context";
import { Sheet } from "../Sheet/Sheet";

type MenuButtonProps = {
    children:
        | React.ReactNode
        | ((toggle: () => void, active?: boolean) => React.ReactNode);
    target: (onClick: () => void, active: boolean) => React.ReactNode;
    position?: "bottom-center" | "bottom-right" | "bottom-left" | "top-left";
    overflow?: "visible" | "auto";
    withinPortal?: boolean;
    initialOpen?: boolean;
    className?: string;
    bodyStyle?: React.CSSProperties;
    withInert?: boolean;
    listenersIgnoreModals?: boolean;
};

const positionPlacement = (
    position: MenuButtonProps["position"],
): Placement => {
    switch (position) {
        case "bottom-center":
            return "bottom";
        case "bottom-right":
            return "bottom-end";
        case "bottom-left":
            return "bottom-start";
        case "top-left":
            return "top-start";
        default:
            return "bottom";
    }
};

const MenuButtonInner = (props: MenuButtonProps) => {
    const {
        children,
        target,
        position,
        overflow = "auto",
        withinPortal = false,
        className,
        bodyStyle,
        withInert = false,
    } = props;
    const { isSheetPresent, setSheetPresent, id } = useSheetContext();
    const { refs, floatingStyles } = useFloating({
        placement: positionPlacement(position),
        middleware: [offset(), flip(), shift()],
        whileElementsMounted: autoUpdate,
        open: isSheetPresent,
    });

    useInert(
        refs.reference as React.RefObject<HTMLElement>,
        isSheetPresent && withInert,
    );

    const toggle = () => {
        setSheetPresent((prev) => !prev);
    };

    if (withinPortal) {
        return (
            <>
                <Wrapper ref={refs.setReference}>
                    {target(toggle, isSheetPresent)}
                    {isSheetPresent && (
                        <Portal>
                            <Body
                                data-sheet={id}
                                ref={refs.setFloating}
                                data-visible={!!isSheetPresent}
                                data-overflow={overflow}
                                style={{
                                    ...floatingStyles,
                                    zIndex: zIndex.mount_everest,
                                    ...bodyStyle,
                                }}
                                className={className}
                            >
                                {typeof children === "function"
                                    ? children(toggle, isSheetPresent)
                                    : children}
                            </Body>
                        </Portal>
                    )}
                </Wrapper>
            </>
        );
    }

    return (
        <>
            <Wrapper ref={refs.setReference}>
                {target(toggle, isSheetPresent)}
                {isSheetPresent && (
                    <Body
                        data-sheet={id}
                        ref={refs.setFloating}
                        data-visible={!!isSheetPresent}
                        data-overflow={overflow}
                        style={floatingStyles}
                        className={className}
                    >
                        {typeof children === "function"
                            ? children(toggle)
                            : children}
                    </Body>
                )}
            </Wrapper>
        </>
    );
};

export default function MenuButton(props: MenuButtonProps) {
    return (
        <Sheet
            initialOpen={props.initialOpen}
            ignoreModals={props.listenersIgnoreModals}
        >
            <MenuButtonInner {...props} />
        </Sheet>
    );
}

const Wrapper = styled.div`
    position: relative;
`;

const Body = styled.div`
    transform-origin: top center;
    background: white;
    border-radius: 8px;
    border: 1px solid ${palette.neutral[200]};
    box-shadow: ${shadows.large};
    padding: 8px;
    z-index: ${zIndex.mount_everest};
    width: 100%;
    max-width: 300px;
    min-width: max-content;

    max-height: 900px;
    scrollbar-width: thin;
    scrollbar-color: ${palette.neutral[200]} white;

    &[data-overflow="auto"] {
        overflow: auto;
    }

    &[data-overflow="visible"] {
        overflow: visible;
    }

    &[data-visible="false"] {
        opacity: 0;
        pointer-events: none;
        visibility: hidden;
        content-visibility: hidden;
    }
    &[data-visible="true"] {
        opacity: 1;
        pointer-events: auto;
        visibility: visible;
        content-visibility: visible;
    }

    &.maw-100 {
        max-width: 100px;
    }

    &.no-padding {
        padding: 0;
    }
`;
